import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usePlaidLink, type PlaidLinkOnSuccess } from 'react-plaid-link'
import { financialApi, type TransactionsParams } from '@/lib/api/financial'
import { getPlaidInstitutionInfo } from '@/lib/plaid/config'
import type { FlagTransactionRequest } from '@/types'

const QUERY_KEYS = {
  connections: (familyId: string, parentUserId?: string) =>
    ['financial', 'connections', familyId, parentUserId] as const,
  transactions: (familyId: string, params?: TransactionsParams) =>
    ['financial', 'transactions', familyId, params] as const,
  spending: (familyId: string, period?: string) =>
    ['financial', 'spending', familyId, period] as const,
}

export function useFinancialConnections(familyId: string | null, parentUserId?: string) {
  const queryClient = useQueryClient()

  // Note: Backend endpoint is /accounts which returns accounts with their connection info
  const accountsQuery = useQuery({
    queryKey: QUERY_KEYS.connections(familyId!, parentUserId),
    queryFn: () => financialApi.getAccounts(familyId!, { parent_user_id: parentUserId }),
    enabled: !!familyId,
    staleTime: 5 * 60 * 1000,
  })

  const disconnectMutation = useMutation({
    mutationFn: (connectionId: string) => financialApi.disconnectConnection(familyId!, connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial', 'connections', familyId] })
    },
  })

  const syncMutation = useMutation({
    mutationFn: () => financialApi.syncFinancial(familyId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial', 'connections', familyId] })
      queryClient.invalidateQueries({ queryKey: ['financial', 'transactions', familyId] })
      queryClient.invalidateQueries({ queryKey: ['financial', 'spending', familyId] })
    },
  })

  return {
    connections: accountsQuery.data ?? [],
    isLoading: accountsQuery.isLoading,
    error: accountsQuery.error,
    refetch: accountsQuery.refetch,

    disconnect: disconnectMutation.mutateAsync,
    sync: syncMutation.mutateAsync,
    isDisconnecting: disconnectMutation.isPending,
    isSyncing: syncMutation.isPending,
  }
}

export function usePlaidLinkConnect(familyId: string | null) {
  const queryClient = useQueryClient()
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchLinkToken = useCallback(async () => {
    if (!familyId) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await financialApi.getLinkToken(familyId)
      setLinkToken(response.link_token)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get link token')
    } finally {
      setIsLoading(false)
    }
  }, [familyId])

  const onSuccess: PlaidLinkOnSuccess = useCallback(
    async (publicToken, metadata) => {
      if (!familyId) return

      const institutionInfo = getPlaidInstitutionInfo(metadata)

      try {
        await financialApi.exchangeToken({
          family_id: familyId,
          public_token: publicToken,
          institution_id: institutionInfo.institutionId,
          institution_name: institutionInfo.institutionName,
        })

        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.connections(familyId) })
        setLinkToken(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to connect account')
      }
    },
    [familyId, queryClient]
  )

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit: () => {
      setLinkToken(null)
    },
  })

  const connect = useCallback(async () => {
    await fetchLinkToken()
  }, [fetchLinkToken])

  // Auto-open Plaid Link when token is ready
  const openPlaidLink = useCallback(() => {
    if (ready && linkToken) {
      open()
    }
  }, [ready, linkToken, open])

  return {
    connect,
    openPlaidLink,
    isReady: ready && !!linkToken,
    isLoading,
    error,
  }
}

export function useTransactions(familyId: string | null, params?: TransactionsParams) {
  const queryClient = useQueryClient()

  const transactionsQuery = useQuery({
    queryKey: QUERY_KEYS.transactions(familyId!, params),
    queryFn: () => financialApi.getTransactions(familyId!, params),
    enabled: !!familyId,
    staleTime: 1 * 60 * 1000,
  })

  const flagMutation = useMutation({
    mutationFn: ({ transactionId, data }: { transactionId: string; data: FlagTransactionRequest }) =>
      financialApi.flagTransaction(familyId!, transactionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial', 'transactions', familyId] })
    },
  })

  const unflagMutation = useMutation({
    mutationFn: (transactionId: string) => financialApi.unflagTransaction(familyId!, transactionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial', 'transactions', familyId] })
    },
  })

  return {
    transactions: transactionsQuery.data?.data ?? [],
    pagination: transactionsQuery.data?.pagination,
    isLoading: transactionsQuery.isLoading,
    error: transactionsQuery.error,
    refetch: transactionsQuery.refetch,

    flagTransaction: flagMutation.mutateAsync,
    unflagTransaction: unflagMutation.mutateAsync,
    isFlagging: flagMutation.isPending,
    isUnflagging: unflagMutation.isPending,
  }
}

export function useSpendingSummary(
  familyId: string | null,
  period?: '7d' | '30d' | '90d',
  parentUserId?: string
) {
  return useQuery({
    queryKey: [...QUERY_KEYS.spending(familyId!, period), parentUserId] as const,
    queryFn: () => financialApi.getSpendingSummary(familyId!, { period, parent_user_id: parentUserId }),
    enabled: !!familyId,
    staleTime: 5 * 60 * 1000,
  })
}
